import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../contexts/LanguageContext';

const RAGProjectCard = ({ site }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Map the compliance enum to a RAG colour. NULL/missing values render
  // grey ("Unknown") rather than falling through to red — a brand-new site
  // hasn't been assessed yet. Per card 47aUEbUC.
  const ragStatus = site?.compliance_status === 'compliant' ? 'green'
    : site?.compliance_status === 'warning' ? 'amber'
    : site?.compliance_status === 'critical' ? 'red'
    : 'grey';

  const ragConfig = {
    green: {
      bg: 'bg-emerald-50 border-emerald-200',
      badge: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      dot: 'bg-emerald-500',
      label: t('compliant'),
      icon: 'CheckCircle2',
      iconColor: 'text-emerald-600'
    },
    amber: {
      bg: 'bg-amber-50 border-amber-200',
      badge: 'bg-amber-100 text-amber-800 border-amber-300',
      dot: 'bg-amber-500',
      label: t('attention'),
      icon: 'AlertTriangle',
      iconColor: 'text-amber-600'
    },
    red: {
      bg: 'bg-red-50 border-red-200',
      badge: 'bg-red-100 text-red-800 border-red-300',
      dot: 'bg-red-500',
      label: t('critical'),
      icon: 'XCircle',
      iconColor: 'text-red-600'
    },
    grey: {
      bg: 'bg-muted border-border',
      badge: 'bg-muted text-muted-foreground border-border',
      dot: 'bg-muted-foreground',
      label: 'Unknown',
      icon: 'HelpCircle',
      iconColor: 'text-muted-foreground'
    }
  };

  const config = ragConfig?.[ragStatus];
  // Only show metrics sourced from real data — never fall back to random/mock.
  // `null` signals "no data yet" so the UI can render an em-dash or hide the progress bar.
  const completion = Number.isFinite(site?.completion_pct) ? site?.completion_pct : null;
  const overdueTasks = Number.isFinite(site?.overdue_tasks) ? site?.overdue_tasks : null;
  const openSnags = Number.isFinite(site?.open_snags) ? site?.open_snags : null;
  const openIncidents = Number.isFinite(site?.open_incidents) ? site?.open_incidents : null;
  const missingDocs = Number.isFinite(site?.missing_docs) ? site?.missing_docs : null;
  const fmt = (v) => v === null ? '—' : v;

  const handleCardClick = () => navigate(`/sites/${site?.id}`);
  const handleMetricClick = (e, tab) => {
    e?.stopPropagation();
    navigate(`/sites/${site?.id}?tab=${tab}`);
  };

  return (
    <div
      className={`rounded-xl border-2 p-5 cursor-pointer hover:shadow-lg transition-all duration-200 ${config?.bg}`}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e?.key === 'Enter' && handleCardClick()}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <div className={`w-3 h-3 rounded-full flex-shrink-0 ${config?.dot}`} />
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${config?.badge}`}>
              {config?.label}
            </span>
          </div>
          <h3 className="font-semibold text-slate-900 text-base truncate font-heading">{site?.site_name}</h3>
          <p className="text-sm text-slate-500 truncate">{site?.client_name || 'No client'}</p>
        </div>
        <Icon name={config?.icon} size={22} className={config?.iconColor} />
      </div>
      {/* Progress Bar — only when real completion data exists */}
      {completion !== null && (
        <div className="mb-4">
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs text-slate-600 font-medium">{t('completion')}</span>
            <span className="text-xs font-bold text-slate-800">{completion}%</span>
          </div>
          <div className="w-full bg-white/60 rounded-full h-2 border border-slate-200">
            <div
              className={`h-2 rounded-full transition-all ${
                ragStatus === 'green' ? 'bg-emerald-500'
                  : ragStatus === 'amber' ? 'bg-amber-500'
                  : ragStatus === 'red' ? 'bg-red-500'
                  : 'bg-muted-foreground'
              }`}
              style={{ width: `${completion}%` }}
            />
          </div>
        </div>
      )}
      {/* Metrics Grid - all clickable */}
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={(e) => handleMetricClick(e, 'tasks')}
          className="bg-white/70 hover:bg-white rounded-lg p-2.5 text-left transition-colors border border-white/50 hover:border-slate-200"
        >
          <div className="flex items-center gap-1.5 mb-1">
            <Icon name="Clock" size={13} className="text-orange-500" />
            <span className="text-xs text-slate-500">{t('overdueTasks')}</span>
          </div>
          <span className={`text-lg font-bold ${overdueTasks > 0 ? 'text-orange-600' : 'text-slate-700'}`}>
            {fmt(overdueTasks)}
          </span>
        </button>

        <button
          onClick={(e) => handleMetricClick(e, 'snags')}
          className="bg-white/70 hover:bg-white rounded-lg p-2.5 text-left transition-colors border border-white/50 hover:border-slate-200"
        >
          <div className="flex items-center gap-1.5 mb-1">
            <Icon name="Wrench" size={13} className="text-blue-500" />
            <span className="text-xs text-slate-500">{t('openSnags')}</span>
          </div>
          <span className={`text-lg font-bold ${openSnags > 0 ? 'text-blue-600' : 'text-slate-700'}`}>
            {fmt(openSnags)}
          </span>
        </button>

        <button
          onClick={(e) => handleMetricClick(e, 'incidents')}
          className="bg-white/70 hover:bg-white rounded-lg p-2.5 text-left transition-colors border border-white/50 hover:border-slate-200"
        >
          <div className="flex items-center gap-1.5 mb-1">
            <Icon name="AlertOctagon" size={13} className="text-red-500" />
            <span className="text-xs text-slate-500">{t('incidents')}</span>
          </div>
          <span className={`text-lg font-bold ${openIncidents > 0 ? 'text-red-600' : 'text-slate-700'}`}>
            {fmt(openIncidents)}
          </span>
        </button>

        <button
          onClick={(e) => handleMetricClick(e, 'documents')}
          className="bg-white/70 hover:bg-white rounded-lg p-2.5 text-left transition-colors border border-white/50 hover:border-slate-200"
        >
          <div className="flex items-center gap-1.5 mb-1">
            <Icon name="FileX" size={13} className="text-purple-500" />
            <span className="text-xs text-slate-500">{t('missingDocs')}</span>
          </div>
          <span className={`text-lg font-bold ${missingDocs > 0 ? 'text-purple-600' : 'text-slate-700'}`}>
            {fmt(missingDocs)}
          </span>
        </button>
      </div>
      {/* Footer */}
      <div className="mt-3 pt-3 border-t border-white/50 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon name="Users" size={13} className="text-slate-400" />
          <span className="text-xs text-slate-500">{site?.worker_count || 0} {t('workers')}</span>
        </div>
        <div className="flex items-center gap-1 text-xs text-slate-500">
          <span>{t('viewSite')}</span>
          <Icon name="ArrowRight" size={13} className="text-slate-400" />
        </div>
      </div>
    </div>
  );
};

export default RAGProjectCard;
